import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import { Can } from '../../shell/Can.js';
import { Button } from '../../../../shared/components/Button.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import {
  listUsers,
  activateUser,
  deactivateUser,
  USER_STATUSES,
  USER_STATUS_LABELS,
  type User,
} from '../../api/usersApi.js';
import { listVendors } from '../../api/vendorsApi.js';

const ACTION_BTN: React.CSSProperties = {
  marginLeft: 8, padding: '2px 10px', borderRadius: 4,
  border: 'none', color: 'var(--color-text-inverse)', cursor: 'pointer',
};

export function UserListPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState<readonly User[] | null>(null);
  const [vendorCodes, setVendorCodes] = useState<ReadonlyMap<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setError(null);
    listUsers({}, token)
      .then((r) => { if (alive) setRows(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  // Vendor attachment is shown by code, not by the raw uuid stored on the login.
  useEffect(() => {
    let alive = true;
    listVendors(token)
      .then((vs) => {
        if (alive) setVendorCodes(new Map(vs.map((v) => [v.vendorId, v.vendorCode])));
      })
      .catch(() => { /* the list stays usable without the vendor column */ });
    return () => { alive = false; };
  }, [token]);

  const filtered = useMemo(() => {
    if (!rows) return null;
    const q = query.trim().toLowerCase();
    return rows.filter((u) => {
      const matchesQuery =
        !q ||
        u.displayName.toLowerCase().includes(q) ||
        u.emailAddress.toLowerCase().includes(q) ||
        (u.employeeCode ?? '').toLowerCase().includes(q);
      const matchesStatus = !statusFilter || u.userStatus === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [rows, query, statusFilter]);

  async function onActivate(userId: string): Promise<void> {
    setBusyUserId(userId); setError(null); setInfo(null);
    try {
      const updated = await activateUser(userId, token);
      setRows((prev) => prev?.map((u) => (u.userId === userId ? updated : u)) ?? prev);
      setInfo(t('users.edit.activated'));
    } catch (e) {
      // Activation refuses an incomplete profile, so the reason must reach the
      // reader here rather than only on the detail screen.
      const message = (e as Error).message;
      if (message.includes('EX-IAM-30')) {
        // The list cannot fix a missing field; the edit form can. The reason
        // travels along so the operator is not left guessing what to fill.
        nav(`/admin/users/${userId}/edit`, { state: { activateError: message } });
        return;
      }
      setError(message);
    } finally {
      setBusyUserId(null);
    }
  }

  async function onDeactivate(userId: string): Promise<void> {
    setBusyUserId(userId); setError(null); setInfo(null);
    try {
      const updated = await deactivateUser(userId, token);
      setRows((prev) => prev?.map((u) => (u.userId === userId ? updated : u)) ?? prev);
      setInfo(t('users.edit.deactivated'));
    } catch (e) {
      // Refused while the person still holds pending work; say why.
      setError((e as Error).message);
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <main aria-label="user-list">
      <h1>{t('users.list.title')}</h1>
      <Can path="/admin/users" action="create">
        <Link to="/admin/users/new">{t('users.list.new')}</Link>
      </Can>
      <input
        aria-label="filter"
        placeholder={t('users.list.filterPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      <select
        aria-label="status-filter"
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
      >
        <option value="">{t('users.list.allStatuses')}</option>
        {USER_STATUSES.map((s) => (
          <option key={s} value={s}>{USER_STATUS_LABELS[s]}</option>
        ))}
      </select>
      {error ? <p role="alert">{error}</p> : null}
      {info ? <p role="status">{info}</p> : null}
      {!rows ? <p>{t('users.list.loading')}</p> : (
        <table aria-label="user-table">
          <thead>
            <tr>
              <th>{t('users.list.col.employeeCode')}</th>
              <th>{t('users.list.col.vendorCode')}</th>
              <th>{t('users.list.col.name')}</th>
              <th>{t('users.list.col.email')}</th>
              <th>{t('users.list.col.mobile')}</th>
              <th>{t('users.list.col.type')}</th>
              <th>{t('users.list.col.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered!.map((u) => (
              <tr key={u.userId}>
                <td>{u.employeeCode ?? '—'}</td>
                <td>{u.vendorId ? (vendorCodes.get(u.vendorId) ?? '…') : '—'}</td>
                <td>{u.displayName}</td>
                <td>{u.emailAddress}</td>
                <td>{u.mobileNumber}</td>
                <td>{u.userType}</td>
                <td>{USER_STATUS_LABELS[u.userStatus] ?? u.userStatus}</td>
                <td>
                  <Link to={`/admin/users/${u.userId}`}>{t('users.list.view')}</Link>
                  <Can path="/admin/users" action="edit">
                    <Link to={`/admin/users/${u.userId}/edit`} style={{ marginLeft: 8 }}>
                      {t('users.detail.edit')}
                    </Link>
                  </Can>
                  {u.userStatus !== 'ACTIVE' && u.userStatus !== 'ARCHIVED' && u.userStatus !== 'LOCKED' ? (
                    <Can path="/admin/users" action="edit">
                      <Button
                        style={{ ...ACTION_BTN, background: 'var(--color-success)' }}
                        disabled={busyUserId === u.userId}
                        data-testid={`activate-${u.userId}`}
                        onClick={() => void onActivate(u.userId)}
                      >
                        {t('users.edit.activate')}
                      </Button>
                    </Can>
                  ) : null}
                  {u.userStatus === 'ACTIVE' ? (
                    <Can path="/admin/users" action="edit">
                      <Button
                        style={{ ...ACTION_BTN, background: 'var(--color-danger)' }}
                        disabled={busyUserId === u.userId}
                        data-testid={`deactivate-${u.userId}`}
                        onClick={() => void onDeactivate(u.userId)}
                      >
                        {t('users.edit.deactivate')}
                      </Button>
                    </Can>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
